// Chat emote substitution — BTTV, FFZ, 7TV
// Fetches emote lists client-side on page load and substitutes codes in .sc-chat-text spans.
// Works on both the dashboard chat panel and the OBS embed overlay.
// Provider precedence (low→high): FFZ global < BTTV global < 7TV global < FFZ channel < BTTV channel < 7TV channel.

let emoteMap = null;

export function esc(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function bttvImg(id, code) {
  id = esc(id);
  return (
    '<img class="chat-emote" src="https://cdn.betterttv.net/emote/' +
    id +
    '/1x"' +
    ' srcset="https://cdn.betterttv.net/emote/' +
    id +
    "/2x 2x," +
    "https://cdn.betterttv.net/emote/" +
    id +
    '/3x 3x"' +
    ' alt="' +
    esc(code) +
    '" title="' +
    esc(code) +
    '">'
  );
}

export function ffzImg(id, code) {
  id = esc(id);
  return (
    '<img class="chat-emote" src="https://cdn.frankerfacez.com/emoticon/' +
    id +
    '/1"' +
    ' srcset="https://cdn.frankerfacez.com/emoticon/' +
    id +
    "/2 2x," +
    "https://cdn.frankerfacez.com/emoticon/" +
    id +
    '/4 4x"' +
    ' alt="' +
    esc(code) +
    '" title="' +
    esc(code) +
    '">'
  );
}

export function sevenTvImg(id, code) {
  id = esc(id);
  return (
    '<img class="chat-emote" src="https://cdn.7tv.app/emote/' +
    id +
    '/1x.webp"' +
    ' srcset="https://cdn.7tv.app/emote/' +
    id +
    "/2x.webp 2x," +
    "https://cdn.7tv.app/emote/" +
    id +
    '/4x.webp 4x"' +
    ' alt="' +
    esc(code) +
    '" title="' +
    esc(code) +
    '">'
  );
}

function addBttvEmotes(emotes, map) {
  (emotes || []).forEach(function (e) {
    if (!e.modifier) map[e.code] = bttvImg(e.id, e.code);
  });
}

function addFfzEmotes(data, map) {
  var sets = (data && data.sets) || {};
  Object.values(sets).forEach(function (set) {
    (set.emoticons || []).forEach(function (e) {
      map[e.name] = ffzImg(e.id, e.name);
    });
  });
}

function addSevenTvEmotes(emotes, map) {
  (emotes || []).forEach(function (e) {
    map[e.name] = sevenTvImg(e.id, e.name);
  });
}

function safeJson(url) {
  return fetch(url)
    .then(function (r) {
      return r.ok ? r.json() : null;
    })
    .catch(function () {
      return null;
    });
}

function fetchEmotes(broadcasterId) {
  var calls = [
    safeJson("https://api.betterttv.net/3/cached/emotes/global"),
    safeJson("https://api.frankerfacez.com/v1/set/global"),
    safeJson("https://7tv.io/v3/emote-sets/global"),
    broadcasterId
      ? safeJson("https://api.betterttv.net/3/cached/users/twitch/" + broadcasterId)
      : Promise.resolve(null),
    broadcasterId
      ? safeJson("https://api.frankerfacez.com/v1/room/id/" + broadcasterId)
      : Promise.resolve(null),
    broadcasterId
      ? safeJson("https://7tv.io/v3/users/twitch/" + broadcasterId)
      : Promise.resolve(null),
  ];

  return Promise.all(calls).then(function (results) {
    var map = {};
    var bttvGlobal = results[0];
    var ffzGlobal = results[1];
    var stvGlobal = results[2];
    var bttvChan = results[3];
    var ffzChan = results[4];
    var stvChan = results[5];

    addFfzEmotes(ffzGlobal, map);
    addBttvEmotes(bttvGlobal, map);
    addSevenTvEmotes(stvGlobal && stvGlobal.emotes, map);
    if (bttvChan) {
      addBttvEmotes((bttvChan.channelEmotes || []).concat(bttvChan.sharedEmotes || []), map);
    }
    addFfzEmotes(ffzChan, map);
    addSevenTvEmotes(stvChan && stvChan.emote_set && stvChan.emote_set.emotes, map);

    return map;
  });
}

function substituteText(text) {
  var words = text.split(" ");
  var changed = false;
  var parts = words.map(function (word) {
    if (!word) return "";
    // Exact match
    if (emoteMap[word]) {
      changed = true;
      return emoteMap[word];
    }
    // Punctuation-adjacent: e.g. "PogChamp," or "(Kappa)"
    var m = word.match(/^([^a-zA-Z0-9]*)([a-zA-Z0-9][a-zA-Z0-9_-]*)([^a-zA-Z0-9]*)$/);
    if (m && emoteMap[m[2]]) {
      changed = true;
      return esc(m[1]) + emoteMap[m[2]] + esc(m[3]);
    }
    return esc(word);
  });
  return changed ? parts.join(" ") : null;
}

function processContainer(root) {
  root.querySelectorAll(".sc-chat-text").forEach(function (span) {
    var html = substituteText(span.textContent || "");
    if (html !== null) span.innerHTML = html;
  });
}

// Intercept Turbo Stream appends before they hit the DOM.
document.addEventListener("turbo:before-stream-render", function (evt) {
  if (!emoteMap || !Object.keys(emoteMap).length) return;
  var stream = evt.detail && evt.detail.newStream;
  if (!stream || stream.getAttribute("target") !== "chat-feed") return;
  var tmpl = stream.querySelector("template");
  if (tmpl) processContainer(tmpl.content);
});

function init() {
  var feed = document.getElementById("chat-feed");
  if (!feed || feed.dataset.emotesLoaded) return;
  feed.dataset.emotesLoaded = "1";

  var broadcasterId = feed.dataset.twitchBroadcasterId || null;
  fetchEmotes(broadcasterId).then(function (map) {
    emoteMap = map;
    processContainer(feed);
  });
}

document.addEventListener("DOMContentLoaded", init);
document.addEventListener("turbo:load", init);
